import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, resolveDirective as _resolveDirective, withDirectives as _withDirectives, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-30351392"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "h-full flex flex-col border border-gray bg-black" }
const _hoisted_2 = { class: "z-10 absolute p-2 top-[10px] left-[10px] rounded-full bg-white bg-opacity-80" }
const _hoisted_3 = { class: "z-10 absolute p-2 flex justify-end right-0 top-0 bg-gray-100" }
const _hoisted_4 = ["onClick"]
const _hoisted_5 = ["innerHTML"]
const _hoisted_6 = {
  key: 1,
  class: "h-full w-full flex items-center justify-center px-8"
}

import { computed, PropType, ref, Ref, watch } from 'vue';
import { uniq } from 'lodash';
import { useQueryClient } from 'vue-query';
import { ElMessageBox, ElNotification } from 'element-plus';

import { getDomainPath } from '@/services';
import { useGetDevicesQuery } from '@/entities/device/hooks';
import { useGetTypesQuery } from '@/entities/type/hooks';
import { useGetInspectionsByInspectionGroupQuery } from '@/entities/inspection/hooks';
import { annotationGroupsQueryName, useCreateAnnotationGroupMutation } from '@/entities/annotation-group/hooks';
import {
  annotationsQueryName,
  useGetAnnotationByLastDeviceInspectionQuery,
  useGetAnnotationQuery, useUpdateAnnotationMutation,
} from '@/entities/annotation/hooks';

import { OSDExposition } from '@/core/components/data-display/OSD/osd.shared';
import { Device } from '@/entities/device/types';
import { Annotation } from '@/entities/annotation/types';
import { InspectionGroup } from '@/entities/inspection-group/types';

import { NkOSD, NkButton, NkGroup, NkHeading } from '@/core/components';

import InspectionViewerAnnotation
  from '@/teams/visionair/components/InspectionViewer/InspectionViewerAnnotation.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'InspectionLinkViewer',
  props: {
  device: {
    type: Number as PropType<Device['id']>,
    required: true,
  },
  inspectionGroup: {
    type: Number as PropType<InspectionGroup['id']>,
    required: true,
  },
  annotation: {
    type: Number as PropType<Annotation['id']>,
    required: true,
  },
},
  emits: ['close'],
  setup(__props) {

const props = __props





const osd = ref<OSDExposition | undefined>();
const queryClient = useQueryClient();

const {
  mutateAsync: updateAnnotationMutation,
  isLoading: updateAnnotationIsLoading,
} = useUpdateAnnotationMutation();
const {
  mutateAsync: createAnnotationGroupMutation,
  isLoading: createAnnotationIsLoading,
} = useCreateAnnotationGroupMutation();

const actionIsLoading = computed(() => updateAnnotationIsLoading.value || createAnnotationIsLoading.value);

const selectedDevice: Ref<Device['id'] | null> = ref(null);

const inspectionsQueryData = computed(() => ({
  inspectionGroupId: props.inspectionGroup,
  deviceId: props.device,
  wantParentDevice: true,
}));
const hasInspectionsQueryData = computed(() => !!(inspectionsQueryData.value.inspectionGroupId && inspectionsQueryData.value.deviceId));
const {
  data: inspections,
  isLoading: inspectionsIsLoading,
} = useGetInspectionsByInspectionGroupQuery(inspectionsQueryData, {
  enabled: hasInspectionsQueryData,
  onSuccess: (data) => {
    if (!selectedDevice.value && data.length) {
      selectedDevice.value = props.device;
    }
  },
});

const deviceQueryFilters: Ref = computed(() => ({ ids: uniq(inspections.value?.map(o => o.device)) }));
const hasDeviceQueryFilters = computed(() => !!deviceQueryFilters.value.ids.length);
const { data: devices } = useGetDevicesQuery({ enabled: hasDeviceQueryFilters }, deviceQueryFilters);

const typeQueryFilters = computed(() => ({ ids: uniq(devices.value?.map(o => o.type)) }));
const hasTypeQueryFilters: Ref = computed(() => !!typeQueryFilters.value.ids.length);
const { data: types } = useGetTypesQuery({ enabled: hasTypeQueryFilters }, typeQueryFilters);

const getDevices = computed(() => devices.value?.map(o => ({
  ...o,
  type: types.value?.find(t => t.id === o.type),
  isReverse: o.deviceValues.find(dv => dv.reference === 'reverseDevice')?.value || false,
})));

const currentInspection = computed(() => inspections.value?.find(o => o.device === selectedDevice.value));
const dziPath = computed(() => getDomainPath(currentInspection.value?.dziPath));

const annotationsQueryData: Ref = computed(() => ({
  device: selectedDevice.value,
  inspection: currentInspection.value?.id,
}));
const hasAnnotationsQueryData = computed(() => !!(annotationsQueryData.value.device && annotationsQueryData.value.inspection));
const { data: annotations } = useGetAnnotationByLastDeviceInspectionQuery(annotationsQueryData, { enabled: hasAnnotationsQueryData });
const getAnnotations = computed(() => annotations.value?.filter(o => o.id !== props.annotation));

const annotationQueryData = computed(() => ({ id: props.annotation }));
const hasAnnotationQueryData = computed(() => !!annotationQueryData.value.id);
const { data: annotation } = useGetAnnotationQuery(annotationQueryData, { enabled: hasAnnotationQueryData });

const isHighlight = ({ annotationGroupTypes }: Annotation) => {
  const normal = annotation.value?.annotationGroupTypes?.normal;
  return annotation.value && normal && annotationGroupTypes?.normal === normal;
};

const refreshLinkState = async (linkedAnnotationId: Annotation['id']) => {
  await queryClient.refetchQueries(annotationGroupsQueryName, { active: true });
  await queryClient.refetchQueries([annotationsQueryName, { id: annotation.value?.id }]);
  await queryClient.refetchQueries([annotationsQueryName, { id: linkedAnnotationId }]);
  await queryClient.refetchQueries(annotationsQueryName, { active: true });
};

const createRelationLink = async (toLinkAnnotation: Annotation) => {
  if (actionIsLoading.value || isHighlight(toLinkAnnotation)) {
    return false;
  }

  const toLinkAnnotationGroupID = toLinkAnnotation.annotationGroupTypes?.normal;
  const groupID = annotation.value?.annotationGroupTypes?.normal;

  if (!annotation.value) {
    return false;
  }

  if (toLinkAnnotationGroupID) {
    await updateAnnotationMutation({
      ...annotation.value,
      annotationGroups: [
        ...annotation.value.annotationGroups.filter((annotationGroupId) => annotationGroupId !== groupID),
        toLinkAnnotationGroupID,
      ],
    });

    await refreshLinkState(toLinkAnnotation.id);
    return true;
  }

  if (groupID) {
    await updateAnnotationMutation({
      ...toLinkAnnotation,
      annotationGroups: [
        ...toLinkAnnotation.annotationGroups.filter((annotationGroupId) => annotationGroupId !== toLinkAnnotationGroupID),
        groupID,
      ],
    });

    await refreshLinkState(toLinkAnnotation.id);
    return true;
  }

  await createAnnotationGroupMutation({
    date: new Date(),
    comment: '',
    type: 'normal',
    ids: [toLinkAnnotation.id, annotation.value.id],
  });

  await refreshLinkState(toLinkAnnotation.id);
  return true;
};

const handleRelationChoice = async (toLinkAnnotation: Annotation) => {
  if (!annotation.value || actionIsLoading.value || isHighlight(toLinkAnnotation)) {
    return false;
  }

  try {
    await ElMessageBox.confirm(
      `Create a link between annotation ${ annotation.value.id } and annotation ${ toLinkAnnotation.id }?`,
      'Confirm annotation link',
      {
        confirmButtonText: 'Confirm',
        cancelButtonText: 'Cancel',
        type: 'warning',
      },
    );

    const hasLinked = await createRelationLink(toLinkAnnotation);

    if (hasLinked) {
      ElNotification({
        type: 'success',
        position: 'bottom-right',
        message: 'Annotation link created.',
      });
    }

    return hasLinked;
  } catch {
    return false;
  }
};

watch(dziPath, (value) => {
  if (value) {
    osd.value?.updateViewer(value);
  }
});

return (_ctx: any,_cache: any) => {
  const _directive_loading = _resolveDirective("loading")!

  return _withDirectives((_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createVNode(_unref(NkButton), {
        title: "",
        onClick: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('close'))),
        type: "white",
        icon: "NkClose",
        circle: ""
      })
    ]),
    _createElementVNode("div", _hoisted_3, [
      _createVNode(_unref(NkGroup), null, {
        default: _withCtx(() => [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(getDevices), (device) => {
            return (_openBlock(), _createElementBlock("div", {
              key: device.id,
              onClick: ($event: any) => (selectedDevice.value = device.id),
              class: "cursor-pointer"
            }, [
              _createElementVNode("span", {
                class: _normalizeClass(["inspection-link__svg-wrapper", {
              'is-highlight': device.id === selectedDevice.value,
              'block rotate-180': device?.isReverse,
            }]),
                innerHTML: device.type?.svg
              }, null, 10, _hoisted_5)
            ], 8, _hoisted_4))
          }), 128))
        ]),
        _: 1
      })
    ]),
    (_unref(currentInspection)?.dziProcessDone && _unref(currentInspection)?.dziPath)
      ? (_openBlock(), _createBlock(_unref(NkOSD), {
          key: 0,
          ref_key: "osd",
          ref: osd,
          class: "w-full flex-1 bg-black",
          options: {
            tileSources: _unref(dziPath),
          }
        }, {
          default: _withCtx(() => [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(getAnnotations), (annotation, i) => {
              return (_openBlock(), _createBlock(InspectionViewerAnnotation, {
                key: annotation.id,
                class: _normalizeClass({
            'cursor-copy': props.annotation && !isHighlight(annotation),
          }),
                title: `Annotation ${annotation.id}`,
                annotation: annotation.id,
                highlight: isHighlight(annotation),
                index: i,
                onClick: ($event: any) => (handleRelationChoice(annotation))
              }, null, 8, ["class", "title", "annotation", "highlight", "index", "onClick"]))
            }), 128))
          ]),
          _: 1
        }, 8, ["options"]))
      : (!_unref(currentInspection)?.dziProcessDone)
        ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
            (!_unref(currentInspection)?.dziPath)
              ? (_openBlock(), _createBlock(_unref(NkHeading), {
                  key: 0,
                  type: "italic"
                }, {
                  default: _withCtx(() => [
                    _createTextVNode(_toDisplayString(_ctx.$t('inspection.viewer.messages.dzi_missing')), 1)
                  ]),
                  _: 1
                }))
              : (_unref(currentInspection)?.dziProgress === 'Missing image')
                ? (_openBlock(), _createBlock(_unref(NkHeading), {
                    key: 1,
                    type: "italic"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('inspection.viewer.messages.missing_image')), 1)
                    ]),
                    _: 1
                  }))
                : (_openBlock(), _createBlock(_unref(NkHeading), {
                    key: 2,
                    type: "italic"
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(_ctx.$t('inspection.viewer.messages.slicing_in_progress')), 1)
                    ]),
                    _: 1
                  }))
          ]))
        : _createCommentVNode("", true)
  ])), [
    [_directive_loading, _unref(inspectionsIsLoading) || _unref(actionIsLoading)]
  ])
}
}

})